import React, { type MouseEvent, type CSSProperties, useCallback } from 'react';
import { Anchor, xcss } from '@atlaskit/primitives';
import ArrowUpRightIcon from '@atlaskit/icon/core/arrow-up-right';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import type { IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { fireUIAnalytics, type UIAnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
import { useIsEmbedMode } from '@atlassian/jira-issue-view-embed-mode/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIsGridNavigationEnabled } from '../../../../../controllers/features/selectors.tsx';
import messages from './messages.tsx';

type Props = {
	issueKey: IssueKey;
	url?: string;
	onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
};
export const OpenIssueLink = ({ issueKey, onClick, url }: Props) => {
	const { formatMessage } = useIntl();
	const isGridNavigationEnabled = useIsGridNavigationEnabled();

	const isEmbedMode = useIsEmbedMode();
	const handleClick = useCallback(
		(e: MouseEvent<HTMLAnchorElement>, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'tableIssueLink');
			onClick?.(e);
		},
		[onClick],
	);

	let style: CSSProperties;
	if (fg('jira_list_merged_column_gate')) {
		if (fg('jira_list_merged_column_inline_create_icon_style')) {
			if (!isGridNavigationEnabled) {
				style = openIssueLinkStyle;
			}
		} else {
			style = buttonOpacityVariableStyle;
		}
	} else {
		style = buttonOpacityVariableStyleOld;
	}

	const linkUrl = url && fg('jira-linked-work-items-targeting-gate') ? url : `/browse/${issueKey}`;
	return (
		<Tooltip
			content={
				fg('nin_list_summary_cta_tooltip_change')
					? formatMessage(messages.openTooltipNew)
					: formatMessage(messages.openTooltip)
			}
		>
			{(triggerProps) => (
				<Anchor
					{...triggerProps}
					aria-label={
						fg('nin_list_summary_cta_tooltip_change')
							? formatMessage(messages.openLabelNew, { issueKey })
							: formatMessage(messages.openLabel, { issueKey })
					}
					href={linkUrl}
					onClick={handleClick}
					xcss={fg('jira_list_merged_column_gate') ? buttonStyles : buttonStylesOld}
					// Cannot provide CSS variable as a compile time value due to various type constraints between
					// primitives + compiled.
					// eslint-disable-next-line jira/react/no-style-attribute,@atlaskit/ui-styling-standard/enforce-style-prop
					style={style}
					{...(isEmbedMode && { target: '_blank', rel: 'noopener' })}
				>
					<ArrowUpRightIcon
						label=""
						{...(fg('jira_list_merged_column_inline_create_icon_style') && { size: 'small' })}
					/>
				</Anchor>
			)}
		</Tooltip>
	);
};

const buttonOpacityVariableStyle = {
	display: 'var(--open-button-display, "none")',
};

const openIssueLinkStyle = {
	display: 'var(--open-button-display, "flex")',
};

/**
 * @deprecated remove when jira_list_merged_column_gate is removed
 */
const buttonOpacityVariableStyleOld = {
	opacity: 'var(--open-button-opacity, 0)',
};

const buttonStyles = xcss({
	alignItems: 'center',
	borderRadius: 'radius.small',
	borderStyle: 'none',
	borderColor: 'color.border',
	borderWidth: '0',
	color: 'color.text',
	display: 'inline-flex',
	height: '24px',
	justifyContent: 'center',
	marginInlineStart: 'auto',
	padding: 'space.050',
	width: '24px',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
		color: 'color.text',
	},
	':focus': {
		color: 'color.text',
	},
	':active': {
		color: 'color.text',
	},
});

/**
 * @deprecated remove when jira_list_merged_column_gate is removed
 */
const buttonStylesOld = xcss({
	alignItems: 'center',
	borderRadius: 'radius.small',
	borderStyle: 'solid',
	borderColor: 'color.border',
	borderWidth: 'border.width',
	color: 'color.text',
	display: 'inline-flex',
	height: '24px',
	justifyContent: 'center',
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	marginLeft: 'auto',
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	paddingTop: 'space.050',
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	paddingRight: 'space.050',
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	paddingBottom: 'space.050',
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	paddingLeft: 'space.050',
	width: '24px',
	':hover': {
		backgroundColor: 'elevation.surface.hovered',
		color: 'color.text',
	},
	':focus': {
		color: 'color.text',
	},
	':active': {
		color: 'color.text',
	},
});
